import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Table from '../ogComponent/table';
import Spinner from '../ogComponent/spinner';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import BreakModal from './breakModal';
import BreaksAccordion from './breakAccordion';
import BreakDeleteConfirmationModal from './deleteConfirm';
import ShiftEditModal from './shifteditModal';

export default function ShiftDetails() {
  const [shift, setShift] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [subShifts, setSubShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [isEditBreakMode, setIsEditBreakMode] = useState(false);
  const [selectedBreak, setSelectedBreak] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const toast = useRef(null);
  const { id } = useParams();
  const newId = Number(id);

  // Access permissions array from Redux store
  const permissions = useSelector((state) => state.emp.permissions);

  const pagePermissions = ["editshiftsID", "deleteshiftsID", "employeedetailsID"];

  const header = [
    { name: "Si No", label: "#", active: true },
    { name: "name", label: "Name", active: true },
    { name: "branch", label: "Branch", active: true },
    { name: "department", label: "Department", active: true },
    { name: "contact_info", label: "Contact Info", active: true },
    { name: "Status", label: "Active", active: true },
  ];

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleUpdateSuccess = (updatedShift) => {
    setShift(updatedShift);
    fetchShiftDetails();
    toast.current.show({
      severity: 'success',
      summary: 'Success',
      detail: 'Shift updated successfully',
      life: 3000,
    });
  };

  const handleOpenBreakModal = () => {
    setIsEditBreakMode(false);
    setSelectedBreak(null);
    setShowBreakModal(true);
  };

  const handleCloseBreakModal = () => {
    setShowBreakModal(false);
    setIsEditBreakMode(false);
    setSelectedBreak(null);
  };

  const handleAddBreak = async (breakData, id) => {
    const token = localStorage.getItem("accessToken");
    if (isEditBreakMode) {
      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API}/api/employee/breaks/${id}/`,
          { ...breakData, shift: newId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Break updated successfully!',
          life: 3000,
        });
        handleCloseBreakModal();
        fetchShiftDetails();
      } catch (error) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Error updating break. Please try again.',
          life: 3000,
        });
        console.error("Error updating break:", error);
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}/api/employee/breaks/`,
          { ...breakData, shift: newId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Break added successfully!',
          life: 3000,
        });
        handleCloseBreakModal();
        fetchShiftDetails();
      } catch (error) {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Error adding break. Please try again.',
          life: 3000,
        });
        console.error("Error adding break:", error);
      }
    }
  };

  const handleEditBreak = (breakItem) => {
    setIsEditBreakMode(true);
    setSelectedBreak(breakItem);
    setShowBreakModal(true);
  };

  const fetchShiftDetails = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError('No token found');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/api/employee/shifts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setShift(response.data);
      setBreaks(response.data.shift_breaks || []);
    } catch (error) {
      console.error("Error fetching shift details:", error);
      setError('Error fetching shift details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShiftDetails();
  }, [id]);

  useEffect(() => {
    const fetchSubShifts = async () => {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        setError('No token found');
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/api/employee/shiftlines-by-shift/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSubShifts(response.data || []);
      } catch (error) {
        console.error("Error fetching sub-shifts:", error);
        setError('Error fetching sub-shifts');
      }
    };

    fetchSubShifts();
  }, [id]);

  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem("accessToken");
      setLoading(true);
      try {
        let url = `${process.env.REACT_APP_API}/api/account/users-list/?shift=${id}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setFilteredEmployees(response.data || []);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [id]);

  const singleSubShift = (id) => {
    navigate(`/user/subshift/${id}`);
  };

  const handleConfirmDelete = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      await axios.delete(
        `${process.env.REACT_APP_API}/api/employee/shifts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOpenDeleteModal(false);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Shift Deleted Successfully',
        life: 3000,
      });
      setTimeout(() => {
        navigate('/user/shifts/');
      }, 3000);
    } catch (error) {
      console.error('Error deleting shift:', error);
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete shift',
        life: 3000,
      });
    }
  };

  const handleDeleteBreak = async (breakId) => {
    const token = localStorage.getItem("accessToken");
    try {
      await axios.delete(
        `${process.env.REACT_APP_API}/api/employee/breaks/${breakId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Break deleted successfully!',
        life: 3000,
      });
      fetchShiftDetails();
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Error deleting break. Please try again.',
        life: 3000,
      });
      console.error("Error deleting break:", error);
    }
  };

  const handleDeleteClick = () => {
    if (shift?.employee_count > 0) {
      toast.current.show({
        severity: 'warn',
        summary: 'Cannot Delete Shift',
        detail: 'This shift has associated employees. Please reassign them before deleting.',
        life: 3000,
      });
    } else {
      setOpenDeleteModal(true);
    }
  };

  const transformEmployeeData = (employees) => {
    if (!employees || !Array.isArray(employees)) return [];
    return employees.map((employee, index) => ({
      id: employee.id,
      'Si No': index + 1,
      name: (
        <td
          className="d-flex gap-3 align-items-center px-0"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          <img
            style={{ width: '25px', height: '25px', flexShrink: 0, borderRadius: '50%' }}
            src={employee.profile_photo ? employee.profile_photo : '/assets/user.png'}
            alt="user img"
          />
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {employee.name || 'N/A'}
          </div>
        </td>
      ),
      branch: employee.branch?.branch_name || 'N/A',
      department: employee.department?.dep_name || 'N/A',
      contact_info: employee.phone_number || 'N/A',
      Status: (
        <div style={{ color: employee.status ? 'green' : 'tomato' }}>
          <span
            style={{
              display: 'inline-block',
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: employee.status ? 'green' : 'tomato',
              marginRight: '5px',
            }}
          ></span>
          {employee.status ? 'Active' : 'Inactive'}
        </div>
      ),
    }));
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const AddSubShift = () => {
    navigate(`/user/subshiftadd/${newId}`);
  };

  const handleRowClick = permissions.includes('employeedetailsID')
    ? (item) => navigate(`/user/empdetails/${item.id}`)
    : undefined;

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  return (
    <>
      <style>
        {`
          @media (max-width: 500px) {
            .modalmainbox {
              min-width: 75% !important;
            }
          }
        `}
      </style>
      <div className="singlePositionContainer px-5 pt-4 w-100">
        <Toast ref={toast} position="bottom-right" />
        <h4 className="text-capitalize">
          {shift?.shift_name} - {shift?.shift_type || 'N/A'}
        </h4>

        <table>
          <tr>
            <td
              className="mb-0 fw-bolder ps-0"
              style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
            >
              Working Hours
            </td>
            <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
              <span className="me-4">:</span>
              {shift?.working_hours || 'N/A'}
            </td>
          </tr>
          <tr>
            <td
              className="mb-0 fw-bolder ps-0"
              style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
            >
              Start Time
            </td>
            <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
              <span className="me-4">:</span>
              {shift?.start_time || 'N/A'}
            </td>
          </tr>
          <tr>
            <td
              className="mb-0 fw-bolder ps-0"
              style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
            >
              End Time
            </td>
            <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
              <span className="me-4">:</span>
              {shift?.end_time || 'N/A'}
            </td>
          </tr>
          <tr>
            <td
              className="mb-0 fw-bolder ps-0"
              style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
            >
              Cut off time
            </td>
            <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
              <span className="me-4">:</span>
              {shift?.cut_off_time || 'N/A'}
            </td>
          </tr>
          <tr>
            <td
              className="mb-0 fw-bolder ps-0"
              style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
            >
              Leave Type
            </td>
            <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
              <span className="me-4">:</span>
              {shift?.leave_type || 'N/A'}
            </td>
          </tr>
          <tr>
            <td
              className="mb-0 fw-bolder ps-0"
              style={{ fontSize: 'clamp(12px, 1.2vw, 13px)' }}
            >
              Employees
            </td>
            <td style={{ fontSize: 'clamp(14px, 1.3vw, 16px)' }}>
              <span className="me-4">:</span>
              {shift?.employee_count || 'N/A'}
            </td>
          </tr>
        </table>

        <div className="employeeTableContainer mt-4 w-100">
          <div className="d-flex justify-content-end mb-3 gap-2 gap-md-3">
            {permissions.includes('editshiftsID') && (
              <button className="btn btn-outline-success" onClick={AddSubShift}>
                Add Sub-Shift <IconPlus stroke={2} />
              </button>
            )}
            {permissions.includes('editshiftsID') && (
              <button
                className="btn btn-outline-success"
                onClick={handleOpenBreakModal}
              >
                Add Break <IconPlus stroke={2} />
              </button>
            )}
            {permissions.includes('editshiftsID') && (
              <button
                className="btn btn-outline-primary"
                onClick={handleEditClick}
              >
                <IconEdit stroke={2} />
              </button>
            )}
            {permissions.includes('deleteshiftsID') && (
              <button
                className="btn btn-outline-danger"
                onClick={handleDeleteClick}
              >
                <IconTrash stroke={2} />
              </button>
            )}
          </div>

          {permissions.includes('editshiftsID') && (
            <>
              {showBreakModal && (
                <BreakModal
                  showModal={showBreakModal}
                  onClose={handleCloseBreakModal}
                  onSubmit={handleAddBreak}
                  initialBreak={selectedBreak}
                  isEditMode={isEditBreakMode}
                />
              )}
              <ShiftEditModal
                open={showEditModal}
                onClose={() => setShowEditModal(false)}
                shift={shift}
                shiftId={id}
                onUpdateSuccess={handleUpdateSuccess}
              />
            </>
          )}

          {permissions.includes('editshiftsID') && breaks.length > 0 ? (
            <BreaksAccordion
              breaks={breaks}
              onEditBreak={handleEditBreak}
              onDeleteBreak={handleDeleteBreak}
              header={'Breaks'}
            />
          ) : permissions.includes('editshiftsID') ? (
            <p>No breaks found.</p>
          ) : null}

          {subShifts.length > 0 ? (
            <div className="mb-3 d-flex gap-2 flex-column">
              <h5>Sub Shifts</h5>
              <div className="d-flex w-100" style={{ gap: '2%' }}>
                {subShifts.map((item, index) => (
                  <div
                    key={item.id}
                    className="border border-2 d-flex align-items-center justify-content-between rounded-3 p-3"
                    style={{ cursor: 'pointer', width: '49%', background: '#00000014' }}
                    onClick={() => singleSubShift(item.id)}
                  >
                    <div>
                      <p className="mb-2 text-capitalize fw-medium">
                        {item.shift_name} - {item.shift_type}
                      </p>
                      <table style={{ fontSize: '15px' }}>
                        <tr>
                          <td className="ps-0">Start Time</td>
                          <td>
                            <span className="me-3">:</span>
                            {item.start_time}
                          </td>
                        </tr>
                        <tr>
                          <td className="ps-0">End time</td>
                          <td>
                            <span className="me-3">:</span>
                            {item.end_time}
                          </td>
                        </tr>
                      </table>
                    </div>
                    <div>
                      <button
                        className="btn"
                        style={{ background: '#bfbdde', fontSize: '15px' }}
                      >
                        view
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p>No sub-shifts found.</p>
          )}

          {filteredEmployees.length > 0 ? (
            <Table
              header={header}
              product={transformEmployeeData(filteredEmployees)}
              onRowClick={handleRowClick}
              loading={loading}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          ) : (
            <p>No employees found in this shift.</p>
          )}
        </div>

        {permissions.includes('deleteshiftsID') && (
          <BreakDeleteConfirmationModal
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            onConfirm={handleConfirmDelete}
            breakName="Shift"
          />
        )}
      </div>
    </>
  );
}